import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, Copy, Flame, Share2, Receipt, User, Users as UsersIcon,
  Trash2, RefreshCw, Camera, Loader2, ImageIcon, Square, Download, MapPin, StickyNote,
} from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import type { SessionDetailViewProps } from "@/ui/view-props";
import { UNIT_VALUE } from "@/lib/score";
import { FullScreenImageViewer } from "@/components/FullScreenImageViewer";
import { PlayerQuickStatsCard } from "@/components/PlayerQuickStatsCard";
import { PlayerAvatar } from "@/components/PlayerAvatar";

function DetailSkeleton() {
  return (
    <div className="space-y-4 animate-fade-in">
      <Skeleton className="h-16 rounded-xl" />
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
      {[...Array(4)].map((_, i) => (
        <Skeleton key={i} className="h-16 rounded-xl" />
      ))}
    </div>
  );
}

export function SessionDetailModernView(p: SessionDetailViewProps) {
  const navigate = useNavigate();
  const playerRoutePrefix = p.isReadOnly ? `/public/${p.targetUserId}/player` : "/player";

  if (p.loading && !p.session) return <DetailSkeleton />;

  if (!p.session) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <div className="text-sm font-semibold">Session not found</div>
        <Link to={p.isReadOnly ? `/public/${p.targetUserId}/sessions` : "/sessions"}>
          <Button variant="outline" size="sm">← Back to sessions</Button>
        </Link>
      </div>
    );
  }

  const buyInUnit = p.buyInUnit ?? UNIT_VALUE;
  const totalPot = p.results.reduce((sum, r) => sum + r.buyIns * buyInUnit, 0);
  const totalWon = p.results.reduce((sum, r) => (r.net > 0 ? sum + r.net : sum), 0);
  const totalExpenses = p.expenses?.reduce((sum, e) => sum + Number(e.amount), 0) || 0;

  const spentVsGainData = p.results.map((r) => ({
    name: r.name.length > 8 ? r.name.slice(0, 8) + "…" : r.name,
    Spent: r.buyIns * buyInUnit,
    Gained: r.cashOut > 0 ? r.cashOut : 0,
  }));

  const handleExport = () => {
    if (!p.onExportXlsx) return;
    
    const svgElement = document.querySelector('.recharts-surface');
    if (svgElement) {
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width * 2;
        canvas.height = img.height * 2;
        if (ctx) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.scale(2, 2);
          ctx.drawImage(img, 0, 0);
        }
        const pngData = canvas.toDataURL("image/png");
        p.onExportXlsx?.(pngData);
      };
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    } else {
      p.onExportXlsx();
    }
  };

  return (
    <div className="space-y-5 pb-24 animate-fade-in">
      {/* ── Header ───────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Link to={p.isReadOnly ? `/public/${p.targetUserId}/sessions` : "/sessions"}>
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="min-w-0">
            {p.isEditing ? (
              <input
                type="datetime-local"
                className="bg-background border border-input rounded-lg px-2.5 py-1.5 text-sm w-full"
                value={p.editedDate ? p.editedDate.substring(0, 16) : ""}
                onChange={(e) => p.setEditedDate?.(e.target.value)}
              />
            ) : (
              <div className="text-sm font-bold truncate">
                {format(new Date(p.session.date), "MMMM d, yyyy")}
              </div>
            )}
            {p.isEditing ? (
              <input
                type="text"
                placeholder="Venue (optional)"
                className="bg-background border border-input rounded-lg px-2.5 py-1.5 text-sm mt-1 w-full"
                value={p.editedVenue ?? ""}
                onChange={(e) => p.setEditedVenue?.(e.target.value)}
              />
            ) : (
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-xs text-muted-foreground">
                  {format(new Date(p.session.date), "h:mm a")}
                </span>
                {p.venue && (
                  <>
                    <span className="text-muted-foreground/40">·</span>
                    <MapPin className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                    <span className="text-xs text-muted-foreground truncate">{p.venue}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 items-center shrink-0">
          {p.isEditing ? (
            <>
              <Button size="sm" onClick={p.onSave} className="h-8">Save</Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => p.setIsEditing?.(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Badge variant={p.session.status === "settled" ? "success" : "default"}>
              {p.session.status === "settled" ? "Settled" : "Open"}
            </Badge>
          )}
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-12">
        <div className="md:col-span-8 space-y-4">
          {/* ── KPI stat cards ──────────────────────────── */}
          <div className="grid grid-cols-3 gap-2">
            <Card className="border-l-2 border-l-success/60">
              <CardContent className="p-3">
                <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Won</div>
                <div className="text-lg font-bold font-mono text-success mt-0.5 leading-none">
                  {formatCurrency(totalWon)}
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-2 border-l-blue-500/60">
              <CardContent className="p-3">
                <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Out</div>
                <div className="text-lg font-bold font-mono text-blue-500 mt-0.5 leading-none">
                  {formatCurrency(p.totalCashOut || 0)}
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-2 border-l-amber-500/60">
              <CardContent className="p-3">
                <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Exp</div>
                <div className="text-lg font-bold font-mono text-amber-600 dark:text-amber-400 mt-0.5 leading-none">
                  {formatCurrency(totalExpenses)}
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="flex justify-between px-1">
             <div className="text-[9px] text-muted-foreground font-bold uppercase tracking-widest section-label">
                Pot: {formatCurrency(totalPot)}
             </div>
             <div className="text-[9px] text-muted-foreground font-bold uppercase tracking-widest section-label">
                {p.results.length} players • {p.expenses?.length || 0} receipts
             </div>
          </div>

          {/* ── Player result rows ──────────────────────── */}
          <div className="space-y-2">
            {p.results.map((r, i) => (
              <Card
                key={r.id}
                className={cn(
                  "transition-shadow duration-150",
                  !p.isEditing && "hover:shadow-card-md",
                )}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {/* Rank / net indicator */}
                      <div className={cn(
                        "h-9 w-9 rounded-xl flex items-center justify-center shrink-0 text-xs font-bold",
                        r.totalNet > 0
                          ? "bg-success/10 text-success"
                          : r.totalNet < 0
                          ? "bg-destructive/10 text-destructive"
                          : "bg-muted text-muted-foreground",
                      )}>
                        {i + 1}
                      </div>

                      <PlayerAvatar 
                        name={r.name} 
                        avatarUrl={r.avatar_url} 
                        size={36}
                        className="shrink-0"
                      />

                      <div
                        className={cn("min-w-0", !p.isEditing && "cursor-pointer")}
                        onClick={p.isEditing ? undefined : () => navigate(`${playerRoutePrefix}/${r.id}`)}
                      >
                        <PlayerQuickStatsCard playerId={r.id} playerName={r.name}>
                          <div className="text-sm font-semibold truncate">{r.name}</div>
                        </PlayerQuickStatsCard>

                        {p.isEditing ? (
                          <div className="flex gap-3 mt-1.5">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Buy-ins</span>
                              <input
                                type="number"
                                className="bg-background border border-input rounded-md w-14 px-1.5 py-0.5 text-xs"
                                value={p.editedResults?.find((er) => er.id === r.id)?.buyIns || 0}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  p.setEditedResults?.(
                                    p.editedResults?.map((er) => (er.id === r.id ? { ...er, buyIns: val } : er)) || [],
                                  );
                                }}
                              />
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Cash-out</span>
                              <input
                                type="number"
                                className="bg-background border border-input rounded-md w-20 px-1.5 py-0.5 text-xs"
                                value={p.editedResults?.find((er) => er.id === r.id)?.cashOut || 0}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  p.setEditedResults?.(
                                    p.editedResults?.map((er) => (er.id === r.id ? { ...er, cashOut: val } : er)) || [],
                                  );
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className="text-[10px] text-blue-500 font-semibold">
                              Spent {formatCurrency(r.buyIns * buyInUnit)}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              Net {r.net >= 0 ? "+" : ""}{formatCurrency(r.net)}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              Exp {r.expensePaid - r.expenseShare >= 0 ? "+" : ""}{formatCurrency(r.expensePaid - r.expenseShare)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <div className={cn(
                        "text-base font-mono font-bold tabular-nums leading-none",
                        r.totalNet > 0
                          ? "text-success"
                          : r.totalNet < 0
                          ? "text-destructive"
                          : "text-muted-foreground",
                      )}>
                        {r.totalNet >= 0 ? "+" : ""}{formatCurrency(r.totalNet)}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1 section-label">Net</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ── Session notes ───────────────────────────── */}
          {p.isEditing ? (
            <div className="space-y-1.5">
              <label className="section-label flex items-center gap-1">
                <StickyNote className="h-3 w-3" />
                Session Note
              </label>
              <textarea
                placeholder="Add a note (e.g. Pre-Diwali game, Suresh left early)"
                className="bg-background border border-input rounded-lg px-3 py-2 text-sm w-full min-h-[72px] resize-y transition-colors focus:outline-none focus:ring-2 focus:ring-ring/60"
                value={p.editedNotes ?? ""}
                onChange={(e) => p.setEditedNotes?.(e.target.value)}
              />
            </div>
          ) : p.notes ? (
            <Card className="bg-muted/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <StickyNote className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="section-label">Note</span>
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{p.notes}</p>
              </CardContent>
            </Card>
          ) : null}

          {/* ── Spent vs Gained chart ───────────────────── */}
          {p.results.length > 0 && (
            <Card>
              <CardContent className="p-4 pb-2">
                <p className="section-label mb-4">Spent vs Gained</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={spentVsGainData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.08)" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip
                      formatter={(v: number) => formatCurrency(v)}
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "10px",
                        fontSize: 12,
                        boxShadow: "var(--shadow-md)",
                      }}
                    />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Bar dataKey="Spent" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} opacity={0.75} />
                    <Bar dataKey="Gained" fill="hsl(158 64% 42%)" radius={[4, 4, 0, 0]} opacity={0.85} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* ── Expenses breakdown ──────────────────────── */}
          {p.expenses && p.expenses.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-muted-foreground" />
                <span className="section-label">Expenses Breakdown</span>
              </div>
              <div className="space-y-2">
                {p.expenses.map((expense) => (
                  <Card key={expense.id} className="bg-muted/15">
                    <CardContent className="p-3.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 space-y-1">
                          <p className="text-sm font-semibold">{expense.description || "Expense"}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                            <div className="flex items-center gap-1 text-[10px] text-muted-foreground font-medium">
                              <User className="h-2.5 w-2.5" />
                              {expense.payer?.name}
                            </div>
                            <div className="flex items-center gap-1 text-[10px] text-muted-foreground font-medium">
                              <UsersIcon className="h-2.5 w-2.5" />
                              {expense.participants?.length} participants
                            </div>
                          </div>
                        </div>
                        <div className="text-sm font-bold font-mono shrink-0">
                          {formatCurrency(expense.amount)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* ── Quick actions ───────────────────────────── */}
          {!p.isEditing && (
            <div className="flex gap-2 pt-3 border-t border-border/60 flex-wrap">
              {p.onExportXlsx && (
                <Button size="sm" variant="outline" onClick={handleExport} className="gap-1.5 h-8">
                  <Download className="h-3.5 w-3.5" /> XLSX
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={p.onShareWhatsApp} className="gap-1.5 h-8 text-green-600 border-green-200 hover:bg-green-50 dark:hover:bg-green-950/20">
                <Share2 className="h-3.5 w-3.5" /> WhatsApp
              </Button>
              {!p.isReadOnly && (
                <>
                  {p.onRematch && (
                    <Button size="sm" variant="outline" onClick={p.onRematch} className="gap-1.5 flex-1 h-8" data-testid="rematch-button">
                      <RefreshCw className="h-3.5 w-3.5" /> Rematch
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => p.setIsEditing?.(true)} className="flex-1 h-8">
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive hover:bg-destructive/8 flex-1 h-8 gap-1.5"
                    onClick={p.onDelete}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Side panel ──────────────────────────────── */}
        <div className="md:col-span-4 space-y-4">
          {/* Roast card */}
          {(p.roast || !p.isReadOnly) && (
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Flame className={cn("h-4 w-4 text-amber-500", p.generating && "animate-pulse")} />
                    <div className="text-sm font-semibold">{p.roast ? "Session Roast" : "Roast"}</div>
                  </div>
                  {p.roastSpeaking && (
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-6 px-2 text-[10px] gap-1"
                      onClick={p.onStopRoast}
                    >
                      <Square className="h-3 w-3 fill-current" /> Stop
                    </Button>
                  )}
                </div>

                {!p.roast ? (
                  <Button className="w-full gap-2 h-9" variant="secondary" onClick={p.onGenerateRoast} disabled={p.generating}>
                    <Flame className="h-4 w-4 text-amber-500" />
                    {p.generating ? "Generating…" : "Generate roast"}
                  </Button>
                ) : (
                  <>
                    <p className="text-sm text-foreground/90 whitespace-pre-wrap italic leading-relaxed">{p.roast}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs" onClick={p.onCopyRoast}>
                        <Copy className="h-3.5 w-3.5" /> Copy
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs" onClick={p.onShareWhatsApp}>
                        <Share2 className="h-3.5 w-3.5" /> Share
                      </Button>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="w-full gap-1.5 h-8 text-xs text-muted-foreground"
                      onClick={p.onGenerateRoast}
                      disabled={p.generating}
                    >
                      <RefreshCw className={cn("h-3.5 w-3.5", p.generating && "animate-spin")} />
                      Re-generate
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Score sheet image */}
          {(p.scoreImageUrl || !p.isReadOnly) && (
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                {p.scoreImageUrl ? (
                  <div className="relative group/img">
                    <FullScreenImageViewer
                      src={p.scoreImageUrl}
                      trigger={
                        <img
                          src={p.scoreImageUrl}
                          alt="Score Sheet"
                          className="w-full h-48 object-cover cursor-pointer group-hover/img:opacity-90 transition-opacity"
                        />
                      }
                    />
                    {!p.isReadOnly && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon"
                            variant="secondary"
                            className="absolute top-2 right-2 h-8 w-8 shadow-card-md backdrop-blur"
                            disabled={p.isUploading}
                          >
                            {p.isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => document.getElementById("camera-detail-m")?.click()}>
                            <Camera className="mr-2 h-4 w-4" /><span>Take Photo</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => document.getElementById("gallery-detail-m")?.click()}>
                            <ImageIcon className="mr-2 h-4 w-4" /><span>Photo Library</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ) : (
                  !p.isReadOnly && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <div className="m-4 flex flex-col items-center justify-center h-28 border-2 border-dashed border-border/60 rounded-xl cursor-pointer hover:bg-muted/30 hover:border-border transition-colors">
                          {p.isUploading ? (
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                          ) : (
                            <>
                              <Camera className="h-6 w-6 text-muted-foreground/60 mb-1.5" />
                              <span className="section-label">Upload Score Sheet</span>
                            </>
                          )}
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center">
                        <DropdownMenuItem onClick={() => document.getElementById("camera-detail-m")?.click()}>
                          <Camera className="mr-2 h-4 w-4" /><span>Take Photo</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => document.getElementById("gallery-detail-m")?.click()}>
                          <ImageIcon className="mr-2 h-4 w-4" /><span>Photo Library</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )
                )}
                <input type="file" accept="image/*" capture="environment" id="camera-detail-m" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) p.onUploadScoreSheet(f); }} />
                <input type="file" accept="image/*" id="gallery-detail-m" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) p.onUploadScoreSheet(f); }} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
